import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './LineChart.css';

const COLORS = {
  'extension-of-self': '#667eea',
  'magical-tool': '#764ba2',
  'person': '#f093fb',
};

const LABELS = {
  'extension-of-self': 'Extension of Self',
  'magical-tool': 'Magical Tool',
  'person': 'Person',
};

export default function LineChart() {
  const svgRef = useRef(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Load scatter data and process into monthly counts
    fetch('/scatter-data.json')
      .then(r => r.json())
      .then(scatterData => {
        const monthlyData = {};

        // Initialize all months
        for (let year = 2023; year <= 2024; year++) {
          for (let month = 1; month <= 12; month++) {
            const key = `${year}-${month.toString().padStart(2, '0')}`;
            monthlyData[key] = {
              'extension-of-self': 0,
              'magical-tool': 0,
              'person': 0,
            };
          }
        }

        // Count products by month and metaphor
        scatterData.forEach(product => {
          const key = `${product.year}-${product.month.toString().padStart(2, '0')}`;
          if (monthlyData[key]) {
            product.metaphorTypes.forEach(metaphor => {
              monthlyData[key][metaphor]++;
            });
          }
        });

        // Convert to array format for D3
        const sortedData = Object.keys(monthlyData)
          .sort()
          .map(key => ({
            date: key,
            ...monthlyData[key],
          }));

        setData(sortedData);
      })
      .catch(e => console.error('Error loading data:', e));
  }, []);

  useEffect(() => {
    if (!data || !svgRef.current) return;

    const width = 1000;
    const height = 500;
    const margin = { top: 80, right: 30, bottom: 80, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Filter data to only include up to August 2024
    const filteredData = data.filter(d => {
      const dateStr = d.date; // Format: "2023-01" or "2024-08"
      return dateStr < '2024-09';
    });

    // Scales
    const xScale = d3
      .scaleTime()
      .domain([new Date('2023-01-01'), new Date('2024-08-31')])
      .range([0, innerWidth]);

    const yMax = Math.max(...filteredData.map(d => Math.max(d['extension-of-self'], d['magical-tool'], d.person)));
    const yScale = d3
      .scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerHeight, 0]);

    // Line generators
    const lineExtension = d3
      .line()
      .x(d => xScale(new Date(d.date)))
      .y(d => yScale(d['extension-of-self']));

    const lineTool = d3
      .line()
      .x(d => xScale(new Date(d.date)))
      .y(d => yScale(d['magical-tool']));

    const linePerson = d3
      .line()
      .x(d => xScale(new Date(d.date)))
      .y(d => yScale(d.person));

    // Draw grid lines
    g.append('g')
      .attr('class', 'grid')
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-innerWidth)
          .tickFormat('')
      );

    // Draw lines
    const lines = [
      { name: 'extension-of-self', generator: lineExtension },
      { name: 'magical-tool', generator: lineTool },
      { name: 'person', generator: linePerson },
    ];

    lines.forEach(({ name, generator }) => {
      g.append('path')
        .datum(filteredData)
        .attr('class', 'line')
        .attr('d', generator)
        .attr('stroke', COLORS[name])
        .attr('stroke-width', 3)
        .attr('fill', 'none')
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round');
    });

    // Draw circles for data points (optional, only show if few enough)
    if (filteredData.length <= 25) {
      lines.forEach(({ name, generator }) => {
        g.selectAll(`.dots-${name}`)
          .data(filteredData)
          .enter()
          .append('circle')
          .attr('cx', d => xScale(new Date(d.date)))
          .attr('cy', d => yScale(d[name]))
          .attr('r', 4)
          .attr('fill', COLORS[name])
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
      });
    }

    // X axis with clearer labels
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(d3.timeMonth.every(3)).tickFormat(d3.timeFormat('%b %Y')));

    xAxis.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', 65)
      .attr('text-anchor', 'middle')
      .attr('fill', '#333')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text('2023 - 2024');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('fill', '#333')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text('Products per Month');

    // Legend - positioned above the grid
    const legend = svg
      .append('g')
      .attr('transform', `translate(${margin.left + 20},${margin.top - 60})`);

    Object.entries(LABELS).forEach((entry, i) => {
      const [key, label] = entry;
      legend
        .append('line')
        .attr('x1', i * 280)
        .attr('x2', i * 280 + 15)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', COLORS[key])
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      legend
        .append('text')
        .attr('x', i * 280 + 20)
        .attr('y', 5)
        .attr('font-size', '13px')
        .attr('fill', '#333')
        .text(label);
    });
  }, [data]);

  return (
    <div className="line-chart-container">
      <h2>Metaphor Adoption Over Time</h2>
      <p>Monthly count of products by metaphor classification (Jan 2023 - Aug 2024)</p>
      <svg ref={svgRef} className="line-chart-svg"></svg>
      <div className="chart-insights">
        <p>
          <strong>What to look for:</strong> Trends show how each metaphor gained or lost prominence over time.
          A rising line indicates increasing adoption of that metaphor in product descriptions.
        </p>
      </div>
    </div>
  );
}
